import type { CPayout, TalentSelf } from "./data";
import { tr, type Lang } from "./lang";
import { mailLanguageName } from "./mailsprache";

/**
 * Dein Profil als Klappabschnitte (R-19, Umbauten 03.11.2026): je Thema eine Zusammenfassung des gespeicherten Stands für den
 * Klappkopf, ein Haken, wenn das Thema vollständig ist, und das Sprungziel aus „Erste Schritte“ bzw. „Konto“ (#adresse,
 * #steuerstatus, #auszahlung, #sprache, #unterschrift). Reine Logik ohne React, damit Tests die Regeln prüfen.
 */

export type ProfilAbschnitt = "persoenlich" | "rechnungsdaten" | "auszahlung" | "social" | "whatsapp" | "sprache" | "profilbild" | "logo" | "unterschrift";

export interface ProfilStand {
  talent: TalentSelf;
  payout: CPayout | null;
  /** Auszahlungswege wählbar (Spalte payout_method vorhanden) */
  methods: boolean;
  signature: { signing_name: string } | null;
  lang: Lang;
  timeZone: string;
}

const HASH: Record<string, ProfilAbschnitt> = {
  vorname: "persoenlich", nachname: "persoenlich", name: "persoenlich", rufname: "persoenlich", email: "persoenlich",
  adresse: "rechnungsdaten", plz: "rechnungsdaten", ort: "rechnungsdaten", land: "rechnungsdaten", steuerstatus: "rechnungsdaten", ustidnruid: "rechnungsdaten", steuernummer: "rechnungsdaten", firma: "rechnungsdaten",
  auszahlung: "auszahlung", kontoinhaber: "auszahlung", iban: "auszahlung", bic: "auszahlung", bank: "auszahlung", bankland: "auszahlung",
  instagram: "social", tiktok: "social", youtube: "social", whatsapp: "whatsapp", sprache: "sprache", unterschrift: "unterschrift", rechnungslogo: "logo", profilbild: "profilbild",
};

/** Felder des Profilformulars je Abschnitt: daran erkennt der Klappkopf „Nicht gespeichert“ nur im geänderten Abschnitt */
export const ABSCHNITT_FELDER: Record<Exclude<ProfilAbschnitt, "profilbild" | "logo" | "unterschrift">, readonly string[]> = {
  persoenlich: ["first_name", "last_name", "display_name", "nickname", "email"],
  rechnungsdaten: ["street", "postal_code", "city", "country", "small_business", "vat_id", "tax_number", "company_name", "legal_form", "register_info"],
  auszahlung: ["holder", "iban", "bic", "bank_name", "bank_country", "payout_method", "account_number", "routing_code", "payout_email"],
  social: ["instagram", "tiktok", "youtube"],
  whatsapp: ["whatsapp_phone", "whatsapp_opt_in"],
  sprache: ["language", "desktop_notifications"],
};

/** Weicht das Formular in den Feldern dieses Abschnitts vom gespeicherten Stand ab? */
export function abschnittGeaendert(key: ProfilAbschnitt, form: object, gespeichert: object): boolean {
  const felder = (ABSCHNITT_FELDER as Record<string, readonly string[] | undefined>)[key];
  const a = form as Record<string, unknown>, b = gespeichert as Record<string, unknown>;
  return !!felder && felder.some((f) => a[f] !== b[f]);
}

/**
 * Prüf-Chat Nachtest (03.11.2026), Befund 14: weicht das ganze Formular vom gespeicherten Stand ab? Nimmt man eine Änderung von
 * Hand zurück, verschwindet die klebrige Speicherleiste wieder (vorher blieb sie, sobald einmal getippt war).
 */
export function formGeaendert(form: object, gespeichert: object): boolean {
  const a = form as Record<string, unknown>, b = gespeichert as Record<string, unknown>;
  return [...new Set([...Object.keys(a), ...Object.keys(b)])].some((k) => {
    const x = a[k], y = b[k];
    return x !== y && JSON.stringify(x ?? null) !== JSON.stringify(y ?? null);
  });
}

/** Abschnitt zum Sprungziel der Adresse („#auszahlung“), sonst null */
export function hashAbschnitt(hash: string | null | undefined): ProfilAbschnitt | null {
  const key = decodeURIComponent((hash ?? "").replace(/^#/, "")).trim();
  return key ? HASH[key] ?? null : null;
}

const voll = (v: string | null | undefined): boolean => !!v && v.trim() !== "";

/** Vollständig (true), unvollständig (false) oder ohne Pflichtangaben (null, kein Haken) */
export function abschnittFertig(key: ProfilAbschnitt, s: ProfilStand): boolean | null {
  const tl = s.talent, p = s.payout;
  switch (key) {
    case "persoenlich": return voll(tl.first_name) && (voll(tl.last_name) || voll(tl.company_name)) && voll(tl.email);
    case "rechnungsdaten": return voll(tl.street) && voll(tl.postal_code) && voll(tl.city) && voll(tl.country) && (tl.small_business || voll(tl.tax_number) || voll(tl.vat_id));
    case "auszahlung": {
      const weg = s.methods ? (p?.payout_method ?? "sepa") : "sepa";
      if (weg === "paypal" || weg === "wise") return voll(p?.payout_email);
      return voll(p?.iban) && voll(p?.bank_account_holder);
    }
    case "social": return voll(tl.instagram_handle) || voll(tl.tiktok_handle) || voll(tl.youtube_handle);
    case "unterschrift": return !!s.signature;
    case "profilbild": return voll(tl.profile_image_path);
    case "logo": return voll(tl.invoice_logo);
    default: return null;
  }
}

/** IBAN nur mit den letzten vier Zeichen („… 0006“) */
export function ibanKurz(iban: string | null | undefined): string | null {
  const s = (iban ?? "").replace(/\s+/g, "");
  return s.length >= 4 ? `… ${s.slice(-4)}` : null;
}

/** Zusammenfassung des gespeicherten Stands für den Klappkopf (leer, wenn nichts eingetragen ist) */
export function abschnittZusammenfassung(key: ProfilAbschnitt, s: ProfilStand): string {
  const t = (de: string, en: string) => tr(s.lang, de, en);
  const tl = s.talent, p = s.payout;
  const teile = (xs: (string | null | undefined)[]) => xs.filter((x): x is string => voll(x)).join(" · ");
  switch (key) {
    case "persoenlich": return teile([[tl.first_name, tl.last_name].filter(voll).join(" "), tl.email]);
    case "rechnungsdaten": {
      const ort = [tl.postal_code, tl.city].filter(voll).join(" ");
      const steuer = tl.small_business ? t("Kleinunternehmer", "Small business") : voll(tl.vat_id) ? tl.vat_id : voll(tl.tax_number) ? t("Steuernummer hinterlegt", "Tax number on file") : null;
      return teile([tl.street, ort, tl.country, tl.company_name, steuer]);
    }
    case "auszahlung": {
      if (!p) return "";
      const weg = s.methods ? (p.payout_method ?? "sepa") : "sepa";
      if (weg === "paypal" || weg === "wise") return teile([weg === "paypal" ? "PayPal" : "Wise", p.payout_email]);
      return teile([weg === "swift" ? "SWIFT" : "SEPA", ibanKurz(p.iban), p.bank_name]);
    }
    case "social": return teile([tl.instagram_handle ? `Instagram ${tl.instagram_handle}` : null, tl.tiktok_handle ? `TikTok ${tl.tiktok_handle}` : null, tl.youtube_handle ? `YouTube ${tl.youtube_handle}` : null]);
    case "whatsapp": return tl.whatsapp_opt_in_at && voll(tl.whatsapp_phone) ? t("Einwilligung erteilt", "Consent given") : voll(tl.whatsapp_phone) ? t("Nummer ohne Einwilligung", "Number without consent") : t("Keine Hinweise per WhatsApp", "No WhatsApp notifications");
    case "sprache": return mailLanguageName(s.lang, s.lang);
    case "profilbild": return voll(tl.profile_image_path) ? t("Bild vorhanden", "Picture on file") : t("Kein Bild", "No picture");
    case "logo": return voll(tl.invoice_logo) ? t("Logo vorhanden", "Logo on file") : t("Kein Logo", "No logo");
    case "unterschrift": return s.signature ? t(`Gespeichert als „${s.signature.signing_name}“`, `Saved as “${s.signature.signing_name}”`) : t("Noch keine Unterschrift", "No signature yet");
  }
}
